import { onCLS } from 'web-vitals/onCLS.js'
import { onINP } from 'web-vitals/onINP.js'
import { onLCP } from 'web-vitals/onLCP.js'
import { onTTFB } from 'web-vitals/onTTFB.js'
import { onFCP } from 'web-vitals/onFCP.js'

const WEB_VITALS_ENDPOINT = process.env.NODE_ENV === 'development' ? process.env.GATSBY_WEB_VITALS_API_URL + '/web-vitals' : process.env.GATSBY_API_URL + '/web-vitals'
const DEBUG = process.env.NODE_ENV === 'development'
const debug = (...args) => {
  if (DEBUG) {
    console.log('[WebVitals]', ...args)
  }
}

const getClientId = () => {
  try {
    let cid = localStorage.getItem('cid')
    if (!cid) {
      cid = Math.random().toString(36).substring(2, 12)
      localStorage.setItem('cid', cid)
    }
    return cid
  } catch (error) {
    console.error('Failed to get client ID:', error)
    return 'anonymous'
  }
}
  
const reportWebVitals = () => {
  const queue = new Set()
  
  const addToQueue = (metric) => {
    try {
      const entry = {
        name: metric.name,
        value: metric.value,
        id: metric.id,
        rating: metric.rating,
        delta: metric.delta,
        cid: getClientId(),
        path: window.location.pathname,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        timestamp: Date.now()
      }
      queue.add(entry)
      debug('Metric queued:', entry.name, entry.value, `(${queue.size} in queue)`)
    } catch (error) {
      console.error(`Failed to add metric ${metric?.name || 'unknown'} to queue:`, error)
    }
  }

  const flushQueue = async (reason = 'manual') => {
    try {
      debug(`Flush triggered (${reason}), queue size: ${queue.size}`)

      if (!WEB_VITALS_ENDPOINT) {
        throw new Error('Web vitals endpoint not configured')
      }

      if (queue.size === 0) {
        debug(`Flush skipped (${reason}): queue is empty`)
        return
      }

      const payload = [...queue]
      const body = JSON.stringify(payload)
      const method = navigator.sendBeacon ? 'sendBeacon' : 'fetch'

      debug(`Sending ${payload.length} metric(s) via ${method} →`, WEB_VITALS_ENDPOINT)
      debug('Payload:', payload)

      // Use sendBeacon when available for more reliable delivery during page unload
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' })
        const success = navigator.sendBeacon(WEB_VITALS_ENDPOINT, blob)
        if (!success) {
          throw new Error('Failed to send metrics using sendBeacon')
        }
      } else {
        // Fall back to fetch with keepalive
        const response = await fetch(WEB_VITALS_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true
        })

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }
      }

      debug(`Flush success (${reason})`)
      queue.clear()
    } catch (error) {
      console.error('Failed to report web vitals batch:', error)
    }
  }

  debug('Reporter initialized, endpoint:', WEB_VITALS_ENDPOINT)

  const monitors = { onCLS, onINP, onLCP, onTTFB, onFCP }
  Object.entries(monitors).forEach(([name, monitor]) => {
    if (typeof monitor !== 'function') {
      debug(`Monitor unavailable: ${name}`)
      return
    }
    monitor(addToQueue)
  })
  
  // Report metrics when page is hidden or unloaded
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushQueue('visibilitychange')
    }
  })
  
  // Periodically flush queue (every 10 seconds) for long-lived sessions
  const intervalId = setInterval(() => flushQueue('interval'), 10000)
  
  // Clean up interval if needed
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      clearInterval(intervalId)
      flushQueue('beforeunload')
    })
  }
}

export default reportWebVitals
